import React, { useCallback, useEffect, useRef, useState } from "react";
import { Users, Plus, MoreVertical, Eye, Ban, CheckCircle, RefreshCw, KeyRound, LogIn, CalendarPlus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import EmptyState from "@/components/common/EmptyState";
import { TableSkeleton } from "@/components/common/Loading";
import Pagination from "@/components/common/Pagination";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PAGE_LIMIT = 25;
const PLANS = ["Starter", "Business", "Pro", "Agency"];
const STATUSES = ["trial", "active", "suspended", "expired"];

const EMPTY_FORM = {
  businessName: "", ownerName: "", email: "", phone: "", username: "", password: "",
  category: "", city: "", plan: "Starter", status: "trial",
  subscriptionStart: "", subscriptionEnd: "", postLimit: "", reviewRequestLimit: "",
  aiReplyLimit: "", allowedLocations: "", monthlyPrice: "",
};

export default function Clients() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const load = useCallback(async (p = 1, q = debouncedSearch, st = filterStatus, pl = filterPlan) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_LIMIT };
      if (q) params.search = q;
      if (st) params.status = st;
      if (pl) params.plan = pl;
      const { data } = await api.get("/admin/clients", { params });
      setRows(arr(data?.data));
      setPagination({ total: data.total, page: data.page, totalPages: data.totalPages });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterStatus, filterPlan]);

  useEffect(() => { load(1, debouncedSearch, filterStatus, filterPlan); }, [debouncedSearch, filterStatus, filterPlan]);

  const handlePage = (p) => { setPage(p); load(p, debouncedSearch, filterStatus, filterPlan); };

  const createClient = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/clients", form);
      toast.success(`Client "${data.client?.businessName}" created`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load(1, debouncedSearch, filterStatus, filterPlan);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const clientAction = async (id, action) => {
    try {
      if (action === "suspend" || action === "activate" || action === "expire") {
        await api.post(`/admin/clients/${id}/${action}`);
        toast.success("Status updated");
      } else if (action === "reset-password") {
        const { data } = await api.post(`/admin/clients/${id}/reset-password`);
        toast.success(`Temp password: ${data.tempPassword}`, { duration: 10000 });
      } else if (action === "login-as") {
        const { data } = await api.post(`/admin/clients/${id}/login-as-client`);
        if (data.token) {
          localStorage.setItem("token", data.token);
          window.location.href = "/dashboard";
        }
      } else if (action === "extend") {
        await api.post(`/admin/clients/${id}/extend-subscription`, { days: 30 });
        toast.success("Subscription extended 30 days");
      }
      load(page, debouncedSearch, filterStatus, filterPlan);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/clients/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.businessName}" deleted`);
      setDeleteTarget(null);
      load(1, debouncedSearch, filterStatus, filterPlan);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clients"
        subtitle={`${pagination.total} total clients`}
        icon={Users}
        actions={
          <Button onClick={() => setShowCreate(true)} className="rounded-xl">
            <Plus className="mr-1 h-4 w-4" /> Add Client
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search name, email, username…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 w-64 rounded-xl border border-slate-200 px-3 text-sm focus:border-brand-500 focus:outline-none"
        />
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterPlan} onChange={(e) => { setFilterPlan(e.target.value); setPage(1); }}
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none">
          <option value="">All Plans</option>
          {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState icon={Users} title="No clients found" description="Add your first client or adjust your filters." />
      ) : (
        <div className="card-surface overflow-hidden p-0">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Google</th>
                  <th className="px-4 py-3">Posts</th>
                  <th className="px-4 py-3">Reviews</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((c) => (
                  <tr key={c.id} className="text-sm hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-slate-800">{c.businessName}</p>
                        <p className="text-xs text-slate-400">{c.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.ownerName || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.plan}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex h-2 w-2 rounded-full ${c.googleConnected ? "bg-green-500" : "bg-slate-300"}`} title={c.googleConnected ? "Connected" : "Not connected"} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.totalPosts}</td>
                    <td className="px-4 py-3 text-slate-600">{c.totalReviews}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {c.subscriptionEnd ? new Date(c.subscriptionEnd).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-slate-100">
                            <MoreVertical className="h-4 w-4 text-slate-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/admin/clients/${c.id}`)}>
                            <Eye className="mr-2 h-4 w-4" /> View / Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => clientAction(c.id, "login-as")}>
                            <LogIn className="mr-2 h-4 w-4" /> Login as Client
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => clientAction(c.id, "reset-password")}>
                            <KeyRound className="mr-2 h-4 w-4" /> Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => clientAction(c.id, "extend")}>
                            <CalendarPlus className="mr-2 h-4 w-4" /> Extend 30 Days
                          </DropdownMenuItem>
                          {c.status === "active" ? (
                            <DropdownMenuItem onClick={() => clientAction(c.id, "suspend")} className="text-amber-600">
                              <Ban className="mr-2 h-4 w-4" /> Suspend
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => clientAction(c.id, "activate")} className="text-green-600">
                              <CheckCircle className="mr-2 h-4 w-4" /> Activate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setDeleteTarget(c)} className="text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-4">
            <Pagination page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} limit={PAGE_LIMIT} onPage={handlePage} />
          </div>
        </div>
      )}

      {/* Create Client Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {[
              ["businessName", "Business Name *"], ["ownerName", "Owner Name"],
              ["email", "Email *"], ["phone", "Phone"],
              ["username", "Username *"], ["password", "Password *"],
              ["category", "Category"], ["city", "City"],
              ["postLimit", "Post Limit"], ["reviewRequestLimit", "Review Request Limit"],
              ["aiReplyLimit", "AI Reply Limit"], ["allowedLocations", "Locations"],
              ["monthlyPrice", "Monthly Price"], ["subscriptionStart", "Sub Start (YYYY-MM-DD)"],
              ["subscriptionEnd", "Sub End (YYYY-MM-DD)"],
            ].map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input
                  type={key === "password" ? "password" : "text"}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="h-9 rounded-xl text-sm"
                />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs">Plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger className="h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createClient} disabled={saving}>{saving ? "Creating…" : "Create Client"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Client</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Permanently delete <strong>{deleteTarget?.businessName}</strong> and all their data? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
